/**
 * Plain CSS port of garage-project-website landing/header/button rules.
 * Injected into the preview iframe so media queries use the device frame width.
 */
export default function getLandingPreviewCss({ blackFontUrl, semiboldFontUrl }) {
  return `
@font-face {
  font-family: 'Sofia Pro';
  src: url('${blackFontUrl}') format('woff2');
  font-weight: 900;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Sofia Pro';
  src: url('${semiboldFontUrl}') format('woff2');
  font-weight: 600;
  font-style: normal;
  font-display: swap;
}

*, *::before, *::after {
  box-sizing: border-box;
}

html {
  font-size: 16px;
}

html, body, #root {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  font-family: 'Sofia Pro', Roboto, ui-sans-serif, system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
}

a {
  color: inherit;
  text-decoration: none;
}

.lp-shell {
  --banner-h: 0px;
  width: 100%;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: #18181b;
  color: #18181b;
}

/*
 * Live stacks AnnouncementBanner (px-4 py-2 + one line of text ≈ 2.5rem)
 * above the h-14 header, so the hero fills viewport − banner − nav.
 * Reserve the same height here so the object-fit: cover crop matches
 * production.
 */
.lp-shell--with-banner {
  --banner-h: 2.5rem;
}

.lp-announcement {
  width: 100%;
  height: var(--banner-h);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  background: #ef6c6a;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
}

/* Live: h-14 (56px) flex-shrink-0 bg-zinc-900 overflow-x-hidden */
.lp-header {
  width: 100%;
  height: 3.5rem;
  max-height: 3.5rem;
  flex-shrink: 0;
  background: #18181b;
  overflow-x: hidden;
  position: relative;
}

.lp-header-inner {
  width: 100%;
  height: 100%;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #fff;
}

@media (min-width: 414px) {
  .lp-header-inner {
    padding: 0 1.5rem;
  }
}

@media (min-width: 896px) {
  .lp-header-inner {
    width: 83.333333%;
    padding: 0;
  }
}

.lp-header-side {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  height: 60%;
}

@media (min-width: 414px) {
  .lp-header-side {
    gap: 1.25rem;
  }
}

.lp-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: #fff;
  display: block;
  flex-shrink: 0;
}

.lp-hamburger {
  width: 1.5rem;
  height: 1.125rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
}

.lp-hamburger span {
  display: block;
  height: 2px;
  width: 100%;
  background: #fff;
  border-radius: 1px;
}

.lp-fresh-logo {
  height: 100%;
  max-height: 100%;
  width: auto;
  max-width: 7rem;
  display: block;
  object-fit: contain;
  object-position: left center;
  filter: brightness(0) invert(1);
}

/*
 * Live expanded logo: fixed, z-30, h-24 / md:h-28.
 * Live uses mt-5/md:mt-7 from the viewport top (overlaps the h-14 bar and
 * paints above it). In the simulator that read as a clipped logo under the
 * nav, so we anchor just below the 3.5rem header — same visual as production
 * with the announcement bar pushing content down.
 */
.lp-logo-overlay {
  position: fixed;
  left: 0;
  top: calc(var(--banner-h) + 3.5rem + 1.5rem);
  width: 100%;
  z-index: 40;
  height: 6rem;
  pointer-events: none;
}

@media (min-width: 768px) {
  .lp-logo-overlay {
    height: 7rem;
  }
}

.lp-logo-overlay-inner {
  width: 83.333333%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
  padding: 0 1rem;
}

/* Live: flex h-full items-center */
.lp-logo-center {
  display: flex;
  height: 100%;
  align-items: center;
  pointer-events: auto;
}

/* Live HeaderLogo link: h-2/3 + SVG fill-current coloured via style.color */
.lp-logo-link {
  display: inline-block;
  height: 66.666667%;
  position: relative;
  color: #fff;
  aspect-ratio: 426.14 / 296.22;
  background-color: currentColor;
  -webkit-mask-image: var(--logo-mask);
  mask-image: var(--logo-mask);
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  filter:
    drop-shadow(0 2px 4px rgb(0 0 0 / 0.4))
    drop-shadow(0 8px 16px rgb(0 0 0 / 0.45));
}

.lp-main {
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  background: #fff;
}

/*
 * Landing hero is full-bleed under the nav (matches live visual).
 * Desktop MainLayout pt-36 clears the expanded logo on inner pages;
 * on the homepage the logo overlays the hero instead — a padded grey
 * band here was reading as a huge thick bar.
 */
.lp-content-pad {
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  padding-top: 0;
}

/* Live hero: border-8 md:border-16 */
.lp-hero {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-style: solid;
  border-color: #fff;
  border-width: 8px;
  box-sizing: border-box;
}

@media (min-width: 768px) {
  .lp-hero {
    border-width: 16px;
  }
}

.lp-bg-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

/* Live GatsbyImage: min-h-full min-w-full inside overflow-hidden.
   Cover fill, centre-cropped to match production. */
.lp-bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.lp-content-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 20;
  pointer-events: none;
}

.lp-homepage {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.lp-spacer {
  flex: 1 1 0%;
  pointer-events: none;
  min-height: 0;
}

/* Live: text-9xl px-10% — base size for %-based fit-text */
.lp-overlay-wrap {
  font-size: 8rem;
  line-height: 1;
  padding-left: 10%;
  padding-right: 10%;
  display: block;
  width: 100%;
  box-sizing: border-box;
}

.lp-overlay-image-wrap {
  display: flex;
  justify-content: center;
  width: 100%;
}

.lp-overlay-image {
  width: auto;
  height: auto;
  max-width: 92vw;
  max-height: 55vh;
  object-fit: contain;
  margin-bottom: 3rem;
  transition: all 0.5s;
}

@media (min-width: 640px) {
  .lp-overlay-image {
    max-width: 72rem;
  }
}

@media (min-width: 768px) {
  .lp-overlay-image {
    max-width: 80rem;
    max-height: 60vh;
    margin-bottom: 0;
  }
}

@media (min-width: 1280px) {
  .lp-overlay-image {
    max-width: 72rem;
  }
}

/* Live h1: flex-1 flex flex-col justify-center items-center text-center font-black uppercase whitespace-pre-line text-white */
.lp-overlay-text {
  flex: 1 1 0%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-weight: 900;
  text-transform: uppercase;
  white-space: pre-line;
  color: #fff;
  line-height: 1;
  margin: 0;
  width: 100%;
  min-width: 0;
}

.lp-overlay-text--hidden {
  visibility: hidden;
}

.lp-overlay-text--visible {
  visibility: visible;
}

.lp-overlay-line {
  white-space: nowrap;
}

.lp-overlay-char {
  display: inline-block;
  vertical-align: top;
  overflow: hidden;
  filter:
    drop-shadow(0 35px 35px rgba(0, 0, 0, 0.25))
    drop-shadow(0 45px 65px rgba(0, 0, 0, 0.15));
}

.lp-overlay-char-inner {
  display: inline-block;
}

/* Live CTAs: flex-1 flex flex-col lg:flex-row gap-x-6 gap-y-4 justify-center items-center */
.lp-ctas {
  flex: 1 1 0%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  column-gap: 1.5rem;
  row-gap: 1rem;
  pointer-events: auto;
}

@media (min-width: 1024px) {
  .lp-ctas {
    flex-direction: row;
  }
}

/* Live .btn from global.css */
.btn {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  min-height: 3.5rem;
  padding-top: 0.5rem;
  padding-bottom: 0.75rem;
  border-radius: 9999px;
  color: #fff;
  font-size: 1rem;
  font-weight: 900;
  text-transform: uppercase;
  line-height: 1.5;
  transition: box-shadow 0.3s ease-out, background-color 0.3s ease-out, transform 0.3s ease-out;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  outline: none;
  min-width: 15rem;
  width: auto;
  max-width: none;
  height: auto;
  white-space: normal;
  padding-left: 2rem;
  padding-right: 2rem;
  max-height: max-content;
  flex-shrink: 0;
  cursor: default;
}

.btn--black {
  background-color: #18181b;
}

.btn--black:hover {
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
  background-color: #f56166;
}

@media (min-width: 414px) {
  .btn {
    height: 3.5rem;
    max-height: 3.5rem;
    white-space: nowrap;
  }
}

@media (min-width: 640px) {
  .btn {
    padding-left: 2.5rem;
    padding-right: 2.5rem;
  }
}

@media (min-width: 1024px) {
  .btn {
    padding-left: 3rem;
    padding-right: 3rem;
  }
}
`;
}
